import { themeVar as colors, type as typeTokens } from "../design-tokens";
import type { ValueAdjustments } from "./types";
import { MarketConditionsPanel, type MarketConditions } from "./MarketConditionsPanel";
import { Band, blockStyle } from "../ui/Block";

/**
 * UPGRADES — TWO INPUTS, NOT ONE (2026-08-01, owner decision).
 *
 * This is the wizard's step 2 and the only genuinely net-new input surface in the flow. Both of
 * the server paths it writes to already existed and had no UI at all:
 *
 *   `meta.value_adjustments` -> `engine.amb.manual_value_adjustments` (engine/amb.py). Each line is
 *       EXACTLY the dollar figure the agent typed, labelled "You entered", applied to the estimate
 *       AFTER the deterministic comparable reconciliation via `shift_headline`. The comparable grid
 *       and the reconciliation note keep showing the untouched comp math, and every entered amount
 *       is added to the QA allow-list so a legitimate agent figure can never trip the honesty gate.
 *       It renders as its own disclosed block in the PDF and the DOCX
 *       (`engine/amb_render._value_adjustments`).
 *
 *   `meta.extra_context`     -> the narrative drafter's per-job context (server/amb_build.py), under
 *       a standing instruction that it may not introduce any number, price, or fact not present in
 *       the evidence. It changes emphasis and wording. It cannot move a figure.
 *
 * WHY THE SPLIT IS THE WHOLE POINT. One combined box — "tell us about the upgrades" — has to have
 * something on the other end converting "new roof, remodeled kitchen" into dollars. Whatever does
 * that conversion, model or heuristic, produces a number with no source, and "every number in this
 * report traces to where it came from" is the product. So the dollar box asks for a dollar figure
 * and attributes it to the person who typed it, and the prose box is told, in the report itself,
 * that it informs the writing and nothing else.
 *
 * WHAT THE DOLLAR FIELDS MEAN, because the engine is specific and the labels have to match it:
 *   - Renovations: the VALUE ADDED, not what it cost. Agents already know these rarely recoup 100%,
 *     and the engine takes the number as given rather than applying a recovery ratio of its own.
 *   - Pool: the engine already prices a pool whenever the subject has one, and this field is the
 *     agent's own figure for THIS pool, applied as the DELTA from that baseline — entering
 *     $30,000 against a $25,000 baseline moves the estimate by $5,000, not $30,000. The baseline
 *     is NOT a fixed number: `engine.amb.pool_adjustment` makes it max($25,000, 5% of the
 *     reconciled value), so it is only knowable after the build runs. That is why nothing on this
 *     screen names it (see the long note in `describeLines`) and why the applied line is shown on
 *     the report screen instead. Only offered when the subject is actually marked as having a
 *     pool — otherwise it is a control that silently does nothing.
 *   - Lot / view: a flat amount on top of the standard lot-size math, with the DIRECTION carrying
 *     the sign. Typing 15000 against "undesirable" subtracts $15,000; there is deliberately no way
 *     to invert it by typing a negative.
 */
export function UpgradesPanel({
  value,
  onChange,
  extraContext,
  onExtraContextChange,
  subjectHasPool,
  marketConditions,
  onMarketConditionsChange,
}: {
  value: ValueAdjustments;
  onChange: (next: ValueAdjustments) => void;
  extraContext: string;
  onExtraContextChange: (next: string) => void;
  /** Whether the confirmed subject grid says this home has a pool. The engine's pool overlay is a
   * no-op without it (`manual_value_adjustments` checks `subject.pool`), so the field is hidden
   * rather than rendered as a box that quietly discards what is typed into it. */
  subjectHasPool?: boolean;
  /** The agent's market-conditions (date of sale) override — `meta.market_conditions`, the top rung
   * of `engine.amb.resolve_market_trend`'s source ladder. OPTIONAL, and the section is omitted
   * entirely when no handler is wired, so a caller that has not adopted it yet renders exactly the
   * panel it rendered before. Unlike the boxes above it this is NOT a value adjustment: it is a
   * rate that produces a real per-comparable row inside the reconciliation grid, which is why it
   * lives in its own section with its own explanation. See `MarketConditionsPanel`. */
  marketConditions?: MarketConditions;
  onMarketConditionsChange?: (next: MarketConditions) => void;
}) {
  function set(patch: Partial<ValueAdjustments>) {
    onChange({ ...value, ...patch });
  }

  const lines = describeLines(value, subjectHasPool);

  return (
    <div style={{ fontFamily: typeTokens.fontUi, color: colors.textPrimary }}>
      {/* THE TWO BOXES, SIDE BY SIDE (D1 option C, 2026-08-29). The paragraph above this panel has
          always said "Two boxes, on purpose. One is for things you can put a number on ... The
          other is for everything else" — and then rendered them one above the other, each capped at
          640 in a 1200 page, with 47% of the glass empty to their right at every desktop width.
          Side by side the sentence is true of the screen, and the page uses the width it has
          without stretching a single field row past the measure it was designed at. `Band` unstacks
          on its own container, so this is still one column on a phone and inside a `prose` page. */}
      <Band>
      <section style={{ marginBottom: 26 }}>
        <h3 className="vb-sec-label">
          Upgrades that carry a dollar figure
        </h3>
        <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 0, marginBottom: 12, lineHeight: 1.5, ...blockStyle("copy") }}>
          These move the estimate. Each one is applied after the comparable reconciliation, shows in
          the report on its own line as <em>You entered</em>, and leaves the comparable grid
          untouched. Leave anything blank and it contributes nothing — nothing here is assumed.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, ...blockStyle("copy") }}>
          <MoneyRow
            label="Renovations and updates"
            hint="The value they ADD, not what they cost. A $60,000 kitchen rarely adds $60,000."
            value={value.reno_value ?? null}
            onChange={(v) => set({ reno_value: v })}
          />

          {subjectHasPool && (
            <MoneyRow
              label="This pool, valued"
              hint="We already price a pool into the estimate. Enter what THIS one is worth and your figure replaces ours — the difference appears on the report as a line with your name on it."
              value={value.pool_value ?? null}
              onChange={(v) => set({ pool_value: v })}
            />
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr",
              alignItems: "start",
              gap: 10,
              padding: "10px 12px",
              background: colors.bgSurface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
            }}
          >
            <label style={{ fontSize: 13 }}>
              Lot or view
              <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                On top of the standard lot-size math. The direction sets the sign — a negative number
                can&apos;t flip it.
              </div>
            </label>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 13 }}>$</span>
              <input
                type="number"
                aria-label="Lot or view amount"
                value={value.lotview_value ?? ""}
                placeholder="—"
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) => set({ lotview_value: e.target.value === "" ? null : Number(e.target.value) })}
                style={{ width: 100, padding: "5px 8px", borderRadius: 6, border: `1px solid ${colors.border}`, fontFamily: "monospace", fontSize: 13 }}
              />
              <select
                aria-label="Lot or view direction"
                value={value.lotview_dir ?? ""}
                onChange={(e) => set({ lotview_dir: (e.target.value || null) as ValueAdjustments["lotview_dir"] })}
                style={{ padding: "5px 8px", borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13 }}
              >
                <option value="">Choose one</option>
                <option value="desirable">Desirable — add</option>
                <option value="undesirable">Undesirable — subtract</option>
              </select>
            </div>
          </div>
        </div>

        {/* What the agent just told the report, said back to them in the report's own words. An
            adjustment the agent cannot see the effect of before generating is one they find out
            about in a PDF they have already sent. */}
        {lines.length > 0 && (
          <div
            role="status"
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 10,
              background: colors.accentSoft,
              border: `1px solid ${colors.border}`,
              ...blockStyle("copy"),
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, fontWeight: 600 }}>
              These will appear in the report, attributed to you:
            </p>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13 }}>
              {lines.map((l) => (
                <li key={l.label} style={{ marginTop: 3 }}>
                  {l.label} — <span style={{ fontFamily: "monospace" }}>{l.shown}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* An amount with no direction is the one input in this panel that is silently discarded by
            the engine, so it is called out here rather than left to be discovered in the output. */}
        {value.lotview_value != null && !value.lotview_dir && (
          <p role="alert" style={{ marginTop: 10, fontSize: 12, color: colors.caution, ...blockStyle("copy") }}>
            Choose desirable or undesirable, or that lot / view amount won&apos;t be applied — we
            won&apos;t assume which way it cuts.
          </p>
        )}
      </section>

      <section>
        <h3 className="vb-sec-label">
          Anything else worth saying
        </h3>
        <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 0, marginBottom: 10, lineHeight: 1.5, ...blockStyle("copy") }}>
          Work you&apos;ve done, what a buyer notices walking in, what the photos don&apos;t show.
          This shapes how the brief is written. It never moves a figure — anything worth a dollar
          amount belongs in a box above, where the report can name you as its source.
        </p>
        <textarea
          aria-label="Anything else worth saying"
          value={extraContext}
          onChange={(e) => onExtraContextChange(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="Roof replaced in 2024. Kitchen taken back to studs — quartz, new cabinets, gas range. The back fence line backs onto the greenbelt rather than another house."
          style={{
            width: "100%",
            ...blockStyle("copy"),
            padding: "10px 12px",
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            fontFamily: typeTokens.fontUi,
            fontSize: 13,
            lineHeight: 1.5,
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        <p style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
          {extraContext.length} / 2000
        </p>
      </section>
      </Band>

      {/* MARKET CONDITIONS COMES OUT OF THE LEFT COLUMN (2026-09-01, W3-UIFIX2).

          It is a RATE, not a value adjustment — it produces a real per-comparable row inside the
          reconciliation grid — and the note that used to sit here said that is why it "stays in
          the left column under them rather than becoming a third thing of its own". Rendered, that
          is what put it in a 640px column with the other column already finished above it and the
          whole right half of a 1920 viewport empty beside it for the length of the block
          (`w2-uifix/after/step2-detail@1920.png`).

          Reading order is unchanged — it is still under the figures — and it is still not a third
          KIND of thing: it is the same section it always was, now taking the page's width instead
          of half of it, and pairing its own explanation with its own controls through the same
          `Band`. */}
      {onMarketConditionsChange && (
        <MarketConditionsPanel
          value={marketConditions ?? {}}
          onChange={onMarketConditionsChange}
        />
      )}
    </div>
  );
}

/** The entered lines, phrased the way the report will phrase them. Deliberately mirrors
 * `engine.amb.manual_value_adjustments`' own labels and its pool-delta arithmetic rather than
 * inventing a second description of the same rule — if the two ever disagree, the agent is being
 * shown one thing and sent another. */
export function describeLines(
  v: ValueAdjustments,
  subjectHasPool?: boolean
): { label: string; shown: string }[] {
  const out: { label: string; shown: string }[] = [];
  const money = (n: number) => `${n < 0 ? "-" : "+"}$${Math.abs(Math.round(n)).toLocaleString()}`;

  const reno = num(v.reno_value);
  if (reno) out.push({ label: "Renovations / updates (value added)", shown: money(reno) });

  const pool = num(v.pool_value);
  // THIS NO LONGER PREDICTS A NUMBER, AND IT CANNOT (2026-08-04).
  //
  // It used to compute `pool - 25000` and print "(baseline $25,000)", mirroring a flat engine
  // constant. `992178b` replaced that constant with a SLIDING baseline —
  // `engine.amb.pool_adjustment(value) = max($25,000, 5% of value)`, resolved from the reconciled
  // point and threaded into `manual_value_adjustments(..., baseline=...)`. This mirror did not
  // move with it, and the divergence is not cosmetic. On a $1.4M reconciled point the engine's
  // baseline is $70,000, so an agent typing $60,000 to say "this pool is worth LESS than you
  // think" was shown, and sent, two different things:
  //
  //     Pool valued at $60,000 (baseline $25,000)      +$35,000   <- what this screen said
  //     Pool valued at $60,000 (baseline $70,000)      -$10,000   <- what the report applied
  //
  // $45,000 apart, opposite signs. And `describeLines` renders twice — in this panel at step 2
  // and again from `ReviewSummary` — so the wrong figure was the last thing read before Generate.
  //
  // THE OBVIOUS FIX IS NOT AVAILABLE. `max(25000, 0.05 * point)` needs the reconciled point, and
  // the point is the OUTPUT of the build the agent has not run yet. There is no honest number to
  // put on this screen, and inventing one is precisely how this broke the first time.
  //
  // So the preview states what the entry MEANS and names no baseline. The real, correctly
  // computed line — label, signed amount, the reconciled figure before and the final after —
  // already comes back from the server on `data.value_adjustments` (amb_build.py:2489) and is now
  // rendered on the report screen, where the point exists. A description that cannot be wrong,
  // and an arithmetic that is always right, each in the only place it can honestly live. The rule
  // this file was written with is unchanged: never keep a second copy of a Python constant in
  // TypeScript.
  if (pool != null && subjectHasPool) {
    out.push({
      label: `This pool, valued by you at $${pool.toLocaleString()}`,
      shown: "replaces ours",
    });
  }

  const lv = num(v.lotview_value);
  if (lv && (v.lotview_dir === "desirable" || v.lotview_dir === "undesirable")) {
    const mag = Math.abs(lv);
    out.push({
      label: `Lot / view (${v.lotview_dir})`,
      shown: money(v.lotview_dir === "desirable" ? mag : -mag),
    });
  }
  return out;
}

function num(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function MoneyRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 1fr",
        alignItems: "start",
        gap: 10,
        padding: "10px 12px",
        background: colors.bgSurface,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
      }}
    >
      <label style={{ fontSize: 13 }}>
        {label}
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>{hint}</div>
      </label>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 13 }}>$</span>
        <input
          type="number"
          aria-label={label}
          value={value ?? ""}
          placeholder="—"
          // UI-064, same reason as the comp grid: a number input eats the wheel gesture and
          // silently changes the value with it.
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          style={{ width: 120, padding: "5px 8px", borderRadius: 6, border: `1px solid ${colors.border}`, fontFamily: "monospace", fontSize: 13 }}
        />
      </span>
    </div>
  );
}
